import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { bootstrapCaService } from '../services/api';

const BootstrapCADetail = () => {
  const { caId } = useParams();
  const navigate = useNavigate();
  const [ca, setCa] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState({ name: '', description: '', validFrom: '', validTo: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCa = async () => {
      try {
        setLoading(true);
        const response = await bootstrapCaService.getById(caId);
        setCa(response.data);
        setEditData({
          name: response.data.name,
          description: response.data.description || '',
          validFrom: response.data.validFrom ? new Date(response.data.validFrom).toISOString().slice(0, 16) : '',
          validTo: response.data.validTo ? new Date(response.data.validTo).toISOString().slice(0, 16) : '',
        });
      } catch (err) {
        console.error('Error fetching Bootstrap CA:', err);
        setError('Failed to load Bootstrap CA details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchCa();
  }, [caId]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  const handleToggleActive = async () => {
    try {
      if (ca.isActive) {
        await bootstrapCaService.deactivate(ca.id, 'Deactivated via admin UI');
        setMessage('Bootstrap CA deactivated');
      } else {
        await bootstrapCaService.activate(ca.id);
        setMessage('Bootstrap CA activated');
      }
      // Refresh data
      const response = await bootstrapCaService.getById(caId);
      setCa(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update Bootstrap CA');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this Bootstrap CA? This cannot be undone.')) {
      return;
    }
    try {
      await bootstrapCaService.delete(ca.id);
      setMessage('Bootstrap CA deleted');
      navigate('/bootstrap-cas');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete Bootstrap CA');
    }
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await bootstrapCaService.update(ca.id, editData);
      setMessage('Bootstrap CA updated');
      setShowEditModal(false);
      // Refresh data
      const response = await bootstrapCaService.getById(caId);
      setCa(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update Bootstrap CA');
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = () => {
    if (!ca.isActive) {
      return <span className="badge badge-gray">⚫ Inactive</span>;
    }
    if (ca.isCurrentlyValid) {
      return <span className="badge badge-green">🟢 Valid</span>;
    }
    return <span className="badge badge-yellow">🟡 Expired</span>;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const getAuthStatusBadge = (status) => {
    const statusMap = {
      success: { color: 'green', label: '✓ Success' },
      missing: { color: 'yellow', label: '⚠ Missing' },
      expired: { color: 'red', label: '✗ Expired' },
      invalid_ca: { color: 'red', label: '✗ Invalid CA' },
      unknown: { color: 'gray', label: '? Unknown' },
    };
    const config = statusMap[status] || statusMap.unknown;
    return <span className={`badge badge-${config.color}`}>{config.label}</span>;
  };

  if (loading) {
    return <div className="loading">Loading Bootstrap CA details...</div>;
  }

  if (error || !ca) {
    return (
      <div className="page">
        <div className="alert alert-error">{error || 'Bootstrap CA not found'}</div>
        <button onClick={() => navigate('/bootstrap-cas')} className="btn btn-secondary">
          ← Back to Bootstrap CAs
        </button>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <button onClick={() => navigate('/bootstrap-cas')} className="btn btn-link">
            ← Back to Bootstrap CAs
          </button>
          <h1>🔐 {ca.name}</h1>
          <p className="page-description">
            {getStatusBadge()}
            <span style={{ marginLeft: '1rem', color: '#666' }}>
              Usage: {ca.usageCount} authentications
            </span>
          </p>
        </div>
        <div className="page-actions" style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setShowEditModal(true)} className="btn btn-secondary">
            ✏️ Edit
          </button>
          <button 
            onClick={handleToggleActive} 
            className={`btn ${ca.isActive ? 'btn-warning' : 'btn-success'}`}
          >
            {ca.isActive ? '⏸️ Deactivate' : '▶️ Activate'}
          </button>
          <button onClick={handleDelete} className="btn btn-danger">
            🗑️ Delete
          </button>
        </div>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="detail-sections">
        {/* CA Information */}
        <div className="detail-card">
          <h2>Certificate Information</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Name</label>
              <div className="detail-value">{ca.name}</div>
            </div>
            <div className="detail-item">
              <label>Description</label>
              <div className="detail-value">{ca.description || 'N/A'}</div>
            </div>
            <div className="detail-item">
              <label>Fingerprint (SHA-256)</label>
              <div className="detail-value code" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                {ca.fingerprint}
              </div>
            </div>
            <div className="detail-item">
              <label>Valid From</label>
              <div className="detail-value">{formatDateTime(ca.validFrom)}</div>
            </div>
            <div className="detail-item">
              <label>Valid To</label>
              <div className="detail-value">{formatDateTime(ca.validTo)}</div>
            </div>
            <div className="detail-item">
              <label>Status</label>
              <div className="detail-value">{getStatusBadge()}</div>
            </div>
          </div>
        </div>

        {/* Usage Statistics */}
        <div className="detail-card">
          <h2>Usage Statistics</h2>
          <div className="detail-grid">
            <div className="detail-item">
              <label>Total Authentications</label>
              <div className="detail-value" style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
                {ca.usageCount}
              </div>
            </div>
            <div className="detail-item">
              <label>Last Used</label>
              <div className="detail-value">{formatDateTime(ca.lastUsedAt)}</div>
            </div>
            <div className="detail-item">
              <label>Created</label>
              <div className="detail-value">{formatDateTime(ca.createdAt)}</div>
            </div>
            <div className="detail-item">
              <label>Created By</label>
              <div className="detail-value">
                {ca.createdBy?.email || ca.createdBy?.name || 'N/A'}
              </div>
            </div>
            {ca.deactivatedAt && (
              <>
                <div className="detail-item">
                  <label>Deactivated</label>
                  <div className="detail-value">{formatDateTime(ca.deactivatedAt)}</div>
                </div>
                <div className="detail-item">
                  <label>Deactivated By</label>
                  <div className="detail-value">
                    {ca.deactivatedBy?.email || ca.deactivatedBy?.name || 'N/A'}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Certificate PEM */}
        <div className="detail-card">
          <h2>Certificate PEM</h2>
          <pre style={{ 
            background: '#f4f4f4', 
            padding: '1rem', 
            borderRadius: '4px', 
            overflow: 'auto',
            fontSize: '0.75rem',
            fontFamily: 'monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all',
          }}>
            {ca.certificatePem}
          </pre>
        </div>

        {/* Recent Auth Logs */}
        <div className="detail-card">
          <h2>Recent Authentication Logs</h2>
          {ca.recentAuthLogs && ca.recentAuthLogs.length > 0 ? (
            <div className="table-container" style={{ overflowX: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Time</th>
                    <th>Status</th>
                    <th>Device</th>
                    <th>IP Address</th>
                    <th>Hardware IDs</th>
                  </tr>
                </thead>
                <tbody>
                  {ca.recentAuthLogs.map(log => (
                    <tr key={log.id}>
                      <td>{formatDateTime(log.createdAt)}</td>
                      <td>{getAuthStatusBadge(log.authStatus)}</td>
                      <td>
                        {log.deviceThingName ? (
                          <Link to={`/ggos-devices/${log.deviceThingName}`}>
                            🖥️ {log.deviceThingName}
                          </Link>
                        ) : (
                          <span className="text-muted">Not Registered</span>
                        )}
                      </td>
                      <td>{log.ipAddress || 'N/A'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {log.hardwareIds ? (
                          <span>
                            {log.hardwareIds.imei && `IMEI: ${log.hardwareIds.imei.substring(0, 8)}...`}
                            {log.hardwareIds.serialNumber && ` SN: ${log.hardwareIds.serialNumber}`}
                          </span>
                        ) : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="empty-state">
              <p>No authentication logs yet</p>
            </div>
          )}
          <div style={{ marginTop: '1rem' }}>
            <Link to={`/bootstrap-auth-logs?caId=${ca.id}`} className="btn btn-secondary">
              View All Auth Logs →
            </Link>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Edit Bootstrap CA</h2>
              <button onClick={() => setShowEditModal(false)} className="modal-close">×</button>
            </div>
            <form onSubmit={handleSaveEdit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editData.description}
                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                    rows={3}
                  />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                  <div className="form-group">
                    <label>Valid From</label>
                    <input
                      type="datetime-local"
                      value={editData.validFrom}
                      onChange={(e) => setEditData({ ...editData, validFrom: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Valid To</label>
                    <input
                      type="datetime-local"
                      value={editData.validTo}
                      onChange={(e) => setEditData({ ...editData, validTo: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowEditModal(false)} className="btn btn-secondary">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="btn btn-primary">
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BootstrapCADetail;
